/**
 * Zeilen-Fabrik des Umsatzsteuer-Rechenkerns: einheitliche Herleitungszeile mit
 * ID-Schema `${type}_${sourceID}_${rule}_${monthKey}`, Netto/Steuer nach R11 und Deep-Links
 * auf die Quelle (Routen aus app.routing.ts, properties.routing.ts, expenses.routing.ts).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {round2, splitGross} from './rounding';
import {kzOfCategory} from './resolve-vat-category';
import {PeriodVatCategory, VatKz, VatLine, VatLineSource, VatMonthSource, VatRate, VatRule, VatSourceType} from './types';

export const lineID = (type: VatSourceType, sourceID: string, rule: VatRule, monthKey: string): string =>
    `${type}_${sourceID}_${rule}_${monthKey}`;

export const round6 = (value: number): number => Math.round(value * 1e6) / 1e6;

// --- Deep-Links -------------------------------------------------------------

export const bookingsDeepLink = (propertyID: string, unitID: string | null): string =>
    unitID ? `/immobilien/${propertyID}/einheit/${unitID}/buchungen` : `/immobilien/${propertyID}/units`;
export const periodDeepLink = (propertyID: string, unitID: string): string => `/immobilien/${propertyID}/einheit/${unitID}/vermietung`;
export const unitsDeepLink = (propertyID: string): string => `/immobilien/${propertyID}/units`;
export const propertyDeepLink = (propertyID: string): string => `/immobilien/${propertyID}/dashboard`;
export const expenseDeepLink = (expenseID: string): string => `/ausgaben/${expenseID}`;
export const setupDeepLink = (landlordID: string): string => `/umsatzsteuer/${landlordID}/einrichtung`;

export const buildSource = (
    type: VatSourceType,
    entity: { id: string; propertyID: string; unitID: string | null; periodID: string | null }
): VatLineSource => ({
    type,
    id: entity.id,
    propertyID: entity.propertyID,
    unitID: entity.unitID,
    periodID: entity.periodID,
    deepLink: type === 'EXPENSE' ? expenseDeepLink(entity.id) : bookingsDeepLink(entity.propertyID, entity.unitID),
});

// --- Kennzahl ---------------------------------------------------------------

export const kzFor = (rule: VatRule, category: PeriodVatCategory | null, rate: VatRate): VatKz => {
    if (rule === 'AUSGESCHLOSSEN' || rule === 'KU_ALTUMSATZ') {
        return 'NONE';
    }
    if (rule === 'VORSTEUER') {
        return '66';
    }
    if (category) {
        return kzOfCategory(category);
    }
    return rate === 19 ? '81' : rate === 7 ? '86' : '48';
};

// --- Zeile ------------------------------------------------------------------

export interface BuildLineParams {
    rule: VatRule;
    source: VatLineSource;
    monthKey: string;
    monthSource: VatMonthSource;
    category: PeriodVatCategory | null;
    rate: VatRate;
    gross: number;               // signiert, vor Anteil
    label: string;
    share?: number;              // Vorsteuer-/Eigentümer-Anteil, Default 1
    tax?: number;                // nur Vorsteuer: Steuerbetrag explizit (vor Anteil)
    issueIDs?: string[];
    idSuffix?: string;           // z. B. Minderungs-ID, damit mehrere Zeilen je Quelle und Monat eindeutig bleiben
    kz?: VatKz;                  // Override, sonst kzFor()
}

export const buildLine = (p: BuildLineParams): VatLine => {
    const share = p.share ?? 1;
    const gross = round2(p.gross * share);
    let net: number;
    let tax: number;
    if (p.tax !== undefined) {
        tax = round2(p.tax * share);
        net = round2(gross - tax);
    } else {
        ({net, tax} = splitGross(gross, p.rate));
    }
    const sourceID = p.idSuffix ? `${p.source.id}:${p.idSuffix}` : p.source.id;
    return {
        id: lineID(p.source.type, sourceID, p.rule, p.monthKey),
        kz: p.kz ?? kzFor(p.rule, p.category, p.rate),
        rule: p.rule,
        source: p.source,
        monthKey: p.monthKey,
        monthSource: p.monthSource,
        category: p.category,
        rate: p.rate,
        gross,
        net,
        tax,
        share: round6(share),
        label: p.label,
        issueIDs: [...(p.issueIDs ?? [])],
    };
};

/**
 * Trägt die Zeile ihren Steuerbetrag explizit (buildLine mit `tax`), statt ihn aus dem Brutto herauszurechnen?
 * Das sind genau die Ausgabenzeilen: Vorsteuer (R9, Steuer laut Beleg) und Nachtrag § 17 auf eine Ausgabe
 * (R7 ADJUSTMENT, Kz 66, gross = tax, net 0). Buchungs- und Zahlungszeilen rechnen die Steuer per Satz aus dem Brutto.
 */
const carriesExplicitTax = (line: VatLine): boolean => line.rule === 'VORSTEUER' || line.source.type === 'EXPENSE';

/** Zeile nachträglich anteilig skalieren (R8 Eigentümerwechsel); ID bleibt stabil. */
export const rescaleLine = (line: VatLine, factor: number): VatLine => {
    const gross = round2(line.gross * factor);
    if (carriesExplicitTax(line)) {
        // Steuer proportional, nie per splitGross – sonst verlöre ein Nachtrag (gross = tax) seinen Kz-66-Betrag
        const tax = round2(line.tax * factor);
        return {...line, gross, tax, net: round2(gross - tax), share: round6(line.share * factor)};
    }
    const {net, tax} = splitGross(gross, line.rate);
    return {...line, gross, net, tax, share: round6(line.share * factor)};
};

export const withIssue = (line: VatLine, issueID: string): VatLine =>
    line.issueIDs.includes(issueID) ? line : {...line, issueIDs: [...line.issueIDs, issueID]};
